import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline'
import { Switch } from '@headlessui/react'
import moment from 'moment'

import placeholderLogo from 'media/placeholder_kinesis_logo.png'
import auth0icon from 'media/icons/auth/auth0.png'
import googleicon from 'media/icons/auth/google.png'
import appleicon from 'media/icons/auth/apple.png'

const accountTypeNames = {
    'trainee': 'Athlete',
    'trainer': 'Coach',
    'organization-admin': 'Organization Admin',
    'organization-owner': 'Organization Owner',
    'super-admin': 'Super Admin'
}

export default function UserDirectoryUserInfoRow({
    user,
    isSelected,
    OnEmailCopyButtonClicked = () => { },
    UserDirectorySubContext
}) {
    return (
        <div
            className={`
                grid w-full min-w-[720px] grid-cols-[repeat(33,minmax(0,1fr))] cursor-pointer hover:bg-gray-100 divide-x divide-gray-400 border-b border-gray-400
                ${isSelected ? 'bg-orange-50' : ''}
            `}
            onClick={() => UserDirectorySubContext.OnUserDirectoryRowPressed(user)}
        >
            <div className='col-span-1 px-1 flex items-center justify-center text-center 2xl:text-sm text-xs'>
                {user.id}
            </div>

            {(user.auth0UserId || '').length > 0 ? (
                <div className='col-span-3 px-2 py-1 truncate min-w-0 flex items-center gap-x-1'>
                    {user.auth0UserId.startsWith('auth0') &&
                        <>
                            <img src={auth0icon} alt="Auth0 Icon" className="w-4 h-4" />
                            <p className='2xl:text-sm text-xs truncate'>Email/Password</p>
                        </>
                    }

                    {user.auth0UserId.startsWith('google') &&
                        <>
                            <img src={googleicon} alt="Google Icon" className="w-4 h-4" />
                            <p className='2xl:text-sm text-xs truncate'>Google</p>
                        </>
                    }

                    {user.auth0UserId.startsWith('apple') &&
                        <>
                            <img src={appleicon} alt="Apple Icon" className="w-4 h-4" />
                            <p className='2xl:text-sm text-xs truncate'>Apple</p>
                        </>
                    }
                </div>
            ) : (
                <div className='col-span-3 px-2 py-1 truncate min-w-0 text-gray-400 2xl:text-sm text-xs'>None</div>
            )}

            <div className="col-span-1 px-2 py-1 flex items-center justify-center">
                <Switch
                    checked={user?.trainingPeaksUserId?.length > 0}
                    className={`
                        scale-[0.8] 2xl:scale-100 relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
                        ${user?.trainingPeaksUserId?.length > 0 ? 'bg-orange-400' : 'bg-gray-200'}
                        data-[disabled]:opacity-90 data-[disabled]:cursor-default
                    `}
                >
                    <span
                        aria-hidden="true"
                        className={`
                            pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                            ${user?.trainingPeaksUserId?.length > 0 ? 'translate-x-4' : 'translate-x-0'}
                        `}
                    />
                </Switch>
            </div>

            <div
                className="col-span-1 px-2 py-1 flex items-center justify-center"
                onClick={(e) => {
                    e.stopPropagation()
                    UserDirectorySubContext.ShowTerraDisconnectionModal(user)
                }}
            >
                <Switch
                    checked={Boolean(user.terraUserId && user.terraUserId.length > 0)}
                    className={`
                        scale-[0.8] 2xl:scale-100 relative inline-flex h-4 w-8 flex-shrink-0 items-center rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out
                        ${user.terraUserId && user.terraUserId.length > 0 ? 'bg-orange-400' : 'bg-gray-200'}
                        data-[disabled]:opacity-90 data-[disabled]:cursor-default
                    `}
                >
                    <span
                        aria-hidden="true"
                        className={`
                            pointer-events-none inline-block h-3 w-3 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out
                            ${user.terraUserId && user.terraUserId.length > 0 ? 'translate-x-4' : 'translate-x-0'}
                        `}
                    />
                </Switch>
            </div>

            <>
                {user.organizationName !== null &&
                    <div className='col-span-4 px-2 py-1 truncate flex items-center gap-x-1 min-w-0'>
                        <img src={user.organizationLogo || placeholderLogo} alt="Organization Logo" className="w-4 h-4 rounded-full flex-shrink-0" />
                        <div className="truncate min-w-0 2xl:text-sm text-xs">{user.organizationName}</div>
                    </div>
                }

                {user.organizationName === null &&
                    <div className='col-span-4 px-2 py-1 truncate text-gray-400 2xl:text-sm text-xs'>Individual</div>
                }
            </>

            <>
                {(user.groupName || '').length > 0 ? (
                    <div className='col-span-3 px-2 py-1 truncate min-w-0 2xl:text-sm text-xs'>
                        {user.groupName}
                    </div>
                ) : (
                    <div className='col-span-3 px-2 py-1 truncate min-w-0 text-gray-400 2xl:text-sm text-xs'>None</div>
                )}
            </>

            <div className='col-span-3 px-2 py-1 truncate 2xl:text-sm text-xs'>{accountTypeNames[user.type]}</div>

            {user?.firstName?.length > 0 ? (
                <div className='col-span-2 px-2 py-1 truncate min-w-0 2xl:text-sm text-xs'>
                    {user.firstName}
                </div>
            ) : (
                <div className='col-span-2 px-2 py-1 truncate min-w-0 text-gray-400 2xl:text-sm text-xs'>
                    None
                </div>
            )}

            {user?.lastName?.length > 0 ? (
                <div className='col-span-2 px-2 py-1 truncate min-w-0 2xl:text-sm text-xs'>
                    {user.lastName}
                </div>
            ) : (
                <div className='col-span-2 px-2 py-1 truncate min-w-0 text-gray-400 2xl:text-sm text-xs'>
                    None
                </div>
            )}

            {user.email !== '' &&
                <div className='col-span-6 pl-2 pr-1 py-1 truncate flex items-center justify-between gap-x-2 min-w-0'>
                    <div className="truncate flex-grow min-w-0 2xl:text-sm text-xs">
                        {user.email}
                    </div>

                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation()
                            OnEmailCopyButtonClicked(user.email)
                        }}
                        className="pr-0.5 hover:bg-gray-100 rounded-full flex-shrink-0"
                        title="Copy email"
                    >
                        <DocumentDuplicateIcon className="h-4 w-4 text-gray-500" />
                    </button>
                </div>
            }

            {user.email === '' && <div className='col-span-6 px-2 py-1 truncate text-gray-400 2xl:text-sm text-xs'>None</div>}

            <div 
                className={`
                    col-span-1 px-2 py-1 truncate min-w-0 2xl:text-sm text-xs text-center font-bold
                    ${user.boilerplateEnabled ? 'bg-green-100 text-green-500' : 'bg-red-100 text-red-500'}
                `}
            >
                {user.boilerplateEnabled ? 'Y' : 'N'}
            </div>

            <div
                className='col-span-4 px-2 py-1 truncate min-w-0 2xl:text-sm text-xs'
                title={`${moment.utc(user.createdDate).local().format('MMMM DD, YYYY')} ${moment.utc(user.createdDate).local().format('hh:mm A')}`}
            >
                {moment.utc(user.createdDate).local().format('MM/DD/YY hh:mm A')}
            </div>

            <div
                className="col-span-2 px-1 py-1 flex items-center justify-center gap-x-3 min-w-0"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    title={isSelected ? 'Deselect user' : 'Select user'}
                    aria-pressed={isSelected}
                    className={`
                        2xl:h-5 2xl:w-5 h-4 w-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors
                        ${isSelected ? 'border-orange-400 bg-orange-400' : 'border-gray-400 bg-white hover:border-orange-300'}
                    `}
                    onClick={(e) => {
                        e.stopPropagation()
                        UserDirectorySubContext.ToggleUserDirectoryUserSelection(user)
                    }}
                >
                    {isSelected &&
                        <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                            <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    }
                </button>

                <button
                    type="button"
                    title="Delete Auth0 Account"
                    className="rounded-md text-gray-600 hover:text-orange-400"
                    onClick={(e) => {
                        e.stopPropagation()
                        UserDirectorySubContext.ShowDeleteUserAuth0AccountModal(user)
                    }}
                >
                    <TrashIcon className="2xl:h-5 2xl:w-5 h-4 w-4" aria-hidden />
                </button>
            </div> 
        </div>
    )
}
